import { IconChevronRight, IconCopy, IconDeviceFloppy, IconMail, IconTrash } from '@tabler/icons-react';

import { Alert, Box, Button, Group } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { validateHandlebarsValue } from '../../helpers/validateHandlebars';
import ErrorMessage from '../ErrorMessage';
import LockableButton from '../Lockable/LockableButton';
import ConfirmationModal from '../Modals/ConfirmationModal';
import Spinner from '../Spinner';
import WarningMessageOnDelete from '../WarningMessageOnDelete';
import CopyEmailToDomainButton from './CopyEmailToDomainButton';
import EmailFormInner from './EmailFormInner';

const tabs = ['content', 'advanced'];

const stripLegacyAmpFields = (email) => {
  if (!email) return email;
  const { ampHtml: _ampHtml, htmlContentType: _htmlContentType, ...rest } = email;
  return rest;
};

const EmailForm = ({
  email: passedEmail,
  saveEmail,
  deleteEmail,
  loading,
  onBack,
  onDuplicate,
  showUserFlow,
  deleteError,
}) => {
  const [email, setEmail] = useState(() => stripLegacyAmpFields(passedEmail));
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [tab, setTab] = useState(tabs[0]);
  const [editorLoaded, setEditorLoaded] = useState(false);
  const withEmailDesignSaveRef = useRef(null);
  const [validation, setValidation] = useState(true);
  useEffect(() => {
    setEmail(stripLegacyAmpFields(passedEmail));
  }, [passedEmail]);

  const senderEmailInvalid =
    email?.senderEmailWithoutDomain &&
    (email.senderEmailWithoutDomain.includes('@') || email.senderEmailWithoutDomain.includes(' '));

  if (!email) {
    return <Spinner />;
  }

  const emailAiOptions = email?.aiEmailOptions;
  const subjectHandlebarsError = validateHandlebarsValue(email.subject);
  const previewHandlebarsError = validateHandlebarsValue(email.preview);
  const senderNameHandlebarsError = validateHandlebarsValue(email.senderName);
  const saveDisabled =
    !email.subject ||
    !email.name ||
    (email.type === 'visual' && !editorLoaded) ||
    !validation ||
    senderEmailInvalid ||
    (emailAiOptions?.isGenerativeEmail && (!emailAiOptions?.productFeedId || !emailAiOptions?.templateId)) ||
    Boolean(subjectHandlebarsError || previewHandlebarsError || senderNameHandlebarsError);

  if (emailAiOptions?.isGenerativeEmail && !emailAiOptions?.numberOfProducts) {
    emailAiOptions.numberOfProducts = 4;
  }

  let deleteButton;
  if (deleteEmail) {
    deleteButton = (
      <LockableButton restrictionKey="emailsDelete" size="sm" color="red" onClick={() => setConfirmDeleteOpen(true)}>
        <IconTrash size={14} /> Delete
      </LockableButton>
    );
  }

  let duplicateButton;
  if (email.id && email.type !== 'custom' && onDuplicate) {
    duplicateButton = (
      <LockableButton restrictionKey="emailsCreate" size="sm" color="cyan" onClick={onDuplicate}>
        <IconCopy size={14} /> Make a copy
      </LockableButton>
    );
  }

  return (
    <div>
      <EmailFormInner
        withEmailDesignSaveRef={withEmailDesignSaveRef}
        email={email}
        updateEmail={(email) => setEmail(email)}
        hasName={true}
        onEditorLoaded={() => setEditorLoaded(true)}
        tab={tab}
        setTab={setTab}
        senderEmailInvalid={senderEmailInvalid}
        subjectHandlebarsError={subjectHandlebarsError}
        previewHandlebarsError={previewHandlebarsError}
        senderNameHandlebarsError={senderNameHandlebarsError}
      />
      {!validation ? (
        <Alert variant="outline" color="red" ta="center" mt="sm">
          <div>
            We have detected errors in your HTML and we recommend you fix them to avoid display issues. You may still
            use this content but it will be at your own responsibility.
          </div>
          <Button onClick={() => setValidation(true)}>I understand and want to save the email.</Button>
        </Alert>
      ) : null}
      <Group justify="flex-end" gap="xs" py="md">
        <Button
          size="sm"
          variant="default"
          onClick={() => {
            if (tabs.indexOf(tab) === 0) {
              onBack();
            }

            setTab(tabs[tabs.indexOf(tab) - 1]);
            window.scrollTo(0, 0);
          }}
        >
          {' '}
          Back
        </Button>
        {deleteButton}
        {duplicateButton}
        {onDuplicate ? <CopyEmailToDomainButton email={email} /> : null}

        {email.id || tabs.indexOf(tab) === tabs.length - 1 ? (
          <>
            {showUserFlow && (
              <Button size="sm" variant="default" onClick={showUserFlow} disabled={saveDisabled || loading}>
                <IconMail size={14} /> Send
              </Button>
            )}
            <LockableButton
              restrictionKey={`emails${email.id ? 'Update' : 'Create'}`}
              size="sm"
              onClick={() => withEmailDesignSaveRef.current(saveEmail, true)}
              disabled={saveDisabled || loading}
              data-tour="emailFormSaveButton"
            >
              <IconDeviceFloppy size={14} /> {loading ? 'Saving...' : 'Save'}
            </LockableButton>
          </>
        ) : (
          <Button
            size="sm"
            data-tour="emailFormNextButton"
            onClick={() => {
              setTab(tabs[tabs.indexOf(tab) + 1]);
              window.scrollTo(0, 0);
            }}
            disabled={!validation}
          >
            Next <IconChevronRight size={14} />
          </Button>
        )}

        <ConfirmationModal
          show={deleteError ? false : confirmDeleteOpen}
          close={() => setConfirmDeleteOpen(false)}
          confirm={(cb) => deleteEmail(cb)}
          message={<WarningMessageOnDelete type="Email" />}
        />
      </Group>
      {deleteError ? (
        <Box ta="right">
          <ErrorMessage message={deleteError} />
        </Box>
      ) : null}
    </div>
  );
};

export default EmailForm;
